import { datadogLogs } from '@datadog/browser-logs';
import { Badge } from '@web-app/components/ui/Badge/badge';
import type { FactorMealSummary } from '@web-app/types/session-prep';
import { FACTOR_MEAL_SUMMARY_LOG_PREFIX } from '@web-app/utils/api/logger-prefixes';
import {
  formatDateRange,
  formatPlanType,
  isCoverageCurrent,
} from '@web-app/utils/factor-meal-summary/factor-meal-summary.utils';
import { FC, useEffect } from 'react';
import { TbToolsKitchen2 } from 'react-icons/tb';

export interface FactorMealSummaryCardProps {
  error: Error | null;
  factorMealSummary: FactorMealSummary | null;
  isLoading: boolean;
  patientId?: string | null;
}

/**
 * Card summarizing a patient's Factor meal-delivery orders.
 *
 * Display logic:
 * - loading → renders nothing. Unlike the core session-prep cards, this card is
 *   only shown for the subset of patients with current Factor orders, so a
 *   skeleton would flash a placeholder that resolves to nothing for everyone
 *   else. We wait until the data is known before committing any layout.
 * - `isFactorPatient: false` → renders nothing (patient has no orders).
 * - coverage window does not include today → renders nothing (stale data).
 * - `summary: null` → deterministic metrics only.
 * - `summary` populated → AI headline + metrics.
 */
export const FactorMealSummaryCard: FC<FactorMealSummaryCardProps> = ({
  error,
  factorMealSummary,
  isLoading,
  patientId,
}) => {
  useEffect(() => {
    if (isLoading) return;

    if (error) {
      datadogLogs.logger.warn(
        `${FACTOR_MEAL_SUMMARY_LOG_PREFIX} Factor summary load failed`,
        { patientId, error: error.message },
      );
    } else if (factorMealSummary?.isFactorPatient) {
      if (isCoverageCurrent(factorMealSummary.dateRange)) {
        datadogLogs.logger.info(
          `${FACTOR_MEAL_SUMMARY_LOG_PREFIX} Factor summary rendered`,
          {
            patientId,
            hasSummary: !!factorMealSummary.summary,
            totalOrders: factorMealSummary.totalOrders,
          },
        );
      } else {
        datadogLogs.logger.info(
          `${FACTOR_MEAL_SUMMARY_LOG_PREFIX} Factor summary hidden (coverage window not current)`,
          { patientId, coverageEnd: factorMealSummary.dateRange?.end ?? null },
        );
      }
    }
  }, [isLoading, error, factorMealSummary, patientId]);

  // Render nothing while loading, on error, when the patient has no Factor
  // orders, or when the summary's coverage window does not include today (stale
  // data). See the component doc for why loading shows no skeleton.
  if (
    isLoading ||
    error ||
    !factorMealSummary?.isFactorPatient ||
    !isCoverageCurrent(factorMealSummary.dateRange)
  ) {
    return null;
  }

  const { summary, mealsPerWeek, planType, totalOrders, dateRange } =
    factorMealSummary;

  const planLabel = formatPlanType(planType);
  const dateRangeLabel = formatDateRange(dateRange);

  return (
    <div
      className="rounded-2xl border border-border bg-card p-6"
      data-testid="factor-meal-summary-card"
    >
      <div className="mb-3 flex items-center gap-2">
        <TbToolsKitchen2 className="size-4 text-primary" />
        <span className="text-xs font-semibold uppercase tracking-[0.5px] text-muted-foreground">
          Factor Meals
        </span>
        {planLabel && (
          <Badge variant="ghost" className="bg-primary-100 text-primary">
            {planLabel}
          </Badge>
        )}
      </div>

      {summary && <p className="mb-3 text-sm text-foreground">{summary}</p>}

      <div className="flex flex-col gap-1">
        {mealsPerWeek != null && (
          <div className="flex items-center justify-between">
            <span className="text-sm text-muted-foreground">
              Meals per week
            </span>
            <span className="text-sm font-semibold text-foreground">
              {mealsPerWeek}
            </span>
          </div>
        )}
        {totalOrders != null && (
          <div className="flex items-center justify-between">
            <span className="text-sm text-muted-foreground">Total orders</span>
            <span className="text-sm font-semibold text-foreground">
              {totalOrders}
            </span>
          </div>
        )}
        {dateRangeLabel && (
          <div className="flex items-center justify-between">
            <span className="text-sm text-muted-foreground">Date range</span>
            <span className="text-sm font-semibold text-foreground">
              {dateRangeLabel}
            </span>
          </div>
        )}
      </div>
    </div>
  );
};

FactorMealSummaryCard.displayName = 'FactorMealSummaryCard';
